import React, { useEffect, useMemo, useState } from "react";
import { X, Clock, MapPin, Star, Euro, Loader2, Route as RouteIcon, Users } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

/**
 * Price — Feuille "Mes trajets".
 *
 * Affiche l'historique COMPLET (tous statuts, pas seulement terminé/
 * annulé) du user courant. Endpoint : GET /api/price/rides/history qui
 * renvoie désormais 3 buckets :
 *   - `live_requests` : demandes Uber-like
 *   - `seat_bookings` : réservations passager sur trajet planifié
 *   - `published_trips` : trajets publiés (drivers uniquement)
 *
 * UX :
 *   - Passager : 1 onglet unique "Mes demandes" (live + réservations)
 *   - Covoitureur : 2 onglets "Mes courses" et "Trajets publiés"
 */

const STATUS_META = {
  // Live requests
  pending: { label: "En attente", bg: "bg-[#FFF4C2]", ring: "border-black" },
  pending_payment: { label: "Paiement", bg: "bg-[#FFF4C2]", ring: "border-black" },
  broadcasting: { label: "Recherche", bg: "bg-[#DCE9FF]", ring: "border-black" },
  accepted: { label: "Accepté", bg: "bg-[#DCE9FF]", ring: "border-black" },
  passenger_boarding: { label: "Embarquement", bg: "bg-[#DCE9FF]", ring: "border-black" },
  in_progress: { label: "En cours", bg: "bg-[#DCE9FF]", ring: "border-black" },
  completed: { label: "Terminé", bg: "bg-[#D6F5D6]", ring: "border-black" },
  cancelled: { label: "Annulé", bg: "bg-[#FFE5E5]", ring: "border-black" },
  expired: { label: "Expiré", bg: "bg-neutral-200", ring: "border-black" },
  scheduled_open: { label: "Programmée", bg: "bg-[#FFF4C2]", ring: "border-black" },
  scheduled_awaiting_payment: { label: "À payer", bg: "bg-[#FFF4C2]", ring: "border-black" },
  scheduled_accepted: { label: "Programmée ✓", bg: "bg-[#D6F5D6]", ring: "border-black" },
  // Trips
  open: { label: "Ouvert", bg: "bg-[#D6F5D6]", ring: "border-black" },
  full: { label: "Complet", bg: "bg-[#DCE9FF]", ring: "border-black" },
  closed: { label: "Fermé", bg: "bg-neutral-200", ring: "border-black" },
};

function StatusBadge({ status }) {
  const m = STATUS_META[status] || { label: status || "—", bg: "bg-neutral-200", ring: "border-black" };
  return (
    <span
      className={
        "text-[10px] font-[Outfit] font-black uppercase tracking-wider px-2 py-0.5 rounded-full border " +
        m.bg + " " + m.ring
      }
    >
      {m.label}
    </span>
  );
}

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric" }) +
      " · " + d.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" });
  } catch { return iso; }
}

function fmtDepartureDate(dt) {
  if (!dt) return "—";
  try {
    const d = new Date(dt);
    return d.toLocaleDateString("fr-FR", { weekday: "short", day: "2-digit", month: "short" }) +
      " · " + d.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" });
  } catch { return dt; }
}

function RideItem({ it, testid }) {
  return (
    <div
      data-testid={testid}
      className={
        "rounded-2xl border-2 border-black p-3 shadow-[0_3px_0_0_#000] " +
        (it.status === "completed" ? "bg-white" : "bg-white")
      }
    >
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-1.5">
          <StatusBadge status={it.status} />
          <span className="text-[10px] font-[Outfit] font-bold text-neutral-500 uppercase">
            {it.kind === "live" ? "Live" : it.kind === "trip" ? "Trajet" : "Réservation"}
          </span>
        </div>
        <div className="flex items-center gap-1 font-[Outfit] font-black text-[15px]">
          <Euro className="w-3.5 h-3.5" strokeWidth={3} />
          {Number(it.price_eur || 0).toFixed(2)}
        </div>
      </div>
      <div className="space-y-1 mb-2">
        <div className="flex items-start gap-2 text-[13px] font-[Outfit]">
          <MapPin className="w-3.5 h-3.5 mt-0.5 shrink-0 text-[#FF3B3B]" strokeWidth={3} />
          <span className="truncate">{it.from_address || "—"}</span>
        </div>
        <div className="flex items-start gap-2 text-[13px] font-[Outfit] text-neutral-700">
          <MapPin className="w-3.5 h-3.5 mt-0.5 shrink-0 text-neutral-500" strokeWidth={3} />
          <span className="truncate">{it.to_address || "—"}</span>
        </div>
      </div>
      <div className="flex items-center justify-between pt-1.5 border-t border-neutral-200">
        <div className="text-[11px] font-[Outfit] text-neutral-600">
          {fmtDate(it.date)}
        </div>
        {typeof it.stars_given === "number" && it.stars_given > 0 && (
          <div className="flex items-center gap-0.5">
            {[1, 2, 3, 4, 5].map((n) => (
              <Star
                key={n}
                className={
                  "w-3 h-3 " +
                  (n <= it.stars_given ? "fill-[#FFB800] text-[#FFB800]" : "text-neutral-300")
                }
                strokeWidth={2}
              />
            ))}
          </div>
        )}
      </div>
      {it.counterpart_name && (
        <div className="text-[11px] font-[Outfit] text-neutral-600 mt-1">
          avec <span className="font-black">{it.counterpart_name}</span>
        </div>
      )}
    </div>
  );
}

function PublishedTripItem({ t, testid }) {
  return (
    <div
      data-testid={testid}
      className="rounded-2xl border-2 border-black p-3 shadow-[0_3px_0_0_#000] bg-white"
    >
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-1.5">
          <StatusBadge status={t.status} />
          <span className="text-[10px] font-[Outfit] font-bold text-neutral-500 uppercase">
            Trajet publié
          </span>
        </div>
        <div className="flex items-center gap-1 font-[Outfit] font-black text-[15px]">
          <Euro className="w-3.5 h-3.5" strokeWidth={3} />
          {Number(t.price_eur || 0).toFixed(2)}
        </div>
      </div>
      <div className="space-y-1 mb-2">
        <div className="flex items-start gap-2 text-[13px] font-[Outfit]">
          <MapPin className="w-3.5 h-3.5 mt-0.5 shrink-0 text-[#FF3B3B]" strokeWidth={3} />
          <span className="truncate">{t.from_address || "—"}</span>
        </div>
        <div className="flex items-start gap-2 text-[13px] font-[Outfit] text-neutral-700">
          <MapPin className="w-3.5 h-3.5 mt-0.5 shrink-0 text-neutral-500" strokeWidth={3} />
          <span className="truncate">{t.to_address || "—"}</span>
        </div>
      </div>
      <div className="flex items-center justify-between pt-1.5 border-t border-neutral-200">
        <div className="text-[11px] font-[Outfit] text-neutral-600">
          <Clock className="w-3 h-3 inline mr-1" strokeWidth={3} />
          {t.departure_at ? fmtDepartureDate(t.departure_at) : (t.departure_date ? `${t.departure_date} ${t.departure_time || ""}` : fmtDate(t.created_at))}
        </div>
        <div className="flex items-center gap-1 text-[11px] font-[Outfit] font-black">
          <Users className="w-3 h-3" strokeWidth={3} />
          <span>{t.seats_taken || 0}/{t.seats_total || 0}</span>
        </div>
      </div>
      {Array.isArray(t.booked_seats) && t.booked_seats.length > 0 && (
        <div className="mt-2 pt-2 border-t border-neutral-200">
          <div className="text-[10px] font-[Outfit] font-bold text-neutral-500 uppercase mb-1">
            Passagers ({t.booked_seats.length})
          </div>
          <div className="flex flex-wrap gap-1">
            {t.booked_seats.map((s) => (
              <span
                key={s.id}
                className="text-[10px] font-[Outfit] font-bold px-2 py-0.5 rounded-full bg-neutral-100 border border-neutral-300"
              >
                {s.client_name || "Passager"} · {s.seats || 1}pl · {STATUS_META[s.status]?.label || s.status}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default function HistorySheet({ open, onClose }) {
  const { user } = useAuth();
  const isDriver = user?.role === "driver";

  const [tab, setTab] = useState("rides"); // "rides" | "trips"
  const [loading, setLoading] = useState(false);
  const [liveItems, setLiveItems] = useState([]);
  const [seatItems, setSeatItems] = useState([]);
  const [trips, setTrips] = useState([]);

  useEffect(() => {
    if (!open) return;
    let cancel = false;
    (async () => {
      setLoading(true);
      try {
        const r = await api.get("/price/rides/history");
        if (cancel) return;
        setLiveItems(Array.isArray(r.data?.live_requests) ? r.data.live_requests : []);
        setSeatItems(Array.isArray(r.data?.seat_bookings) ? r.data.seat_bookings : []);
        setTrips(Array.isArray(r.data?.published_trips) ? r.data.published_trips : []);
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Impossible de charger l'historique");
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    return () => { cancel = true; };
  }, [open]);

  // Pour les passagers : union live + seat bookings triés par date DESC.
  const passengerItems = useMemo(() => {
    const all = [...liveItems, ...seatItems];
    all.sort((a, b) => (b.date || "").localeCompare(a.date || ""));
    return all;
  }, [liveItems, seatItems]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[70] flex items-end sm:items-center sm:justify-center bg-black/50 backdrop-blur-sm"
      data-testid="history-sheet"
      onClick={onClose}
    >
      <div
        className="w-full sm:max-w-md bg-white rounded-t-3xl sm:rounded-3xl border-2 border-black shadow-[0_8px_0_0_#000] max-h-[85vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b-2 border-black bg-[#FFE5E5]">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-xl bg-[#FF3B3B] border-2 border-black grid place-items-center shadow-[0_3px_0_0_#000]">
              <RouteIcon className="w-4 h-4 text-white" strokeWidth={3} />
            </div>
            <div>
              <div className="font-[Outfit] text-[16px] font-black leading-none">Mes trajets</div>
              <div className="font-[Outfit] text-[11px] text-neutral-600 mt-1">
                Récap de toutes tes courses et demandes
              </div>
            </div>
          </div>
          <button
            type="button"
            data-testid="history-close"
            onClick={onClose}
            className="w-9 h-9 rounded-xl border-2 border-black grid place-items-center bg-white active:translate-y-[2px] transition-transform"
          >
            <X className="w-4 h-4" strokeWidth={3} />
          </button>
        </div>

        {/* Tabs — seulement pour driver (rides vs published trips) */}
        {isDriver && (
          <div className="flex gap-2 px-4 pt-3 pb-2 border-b-2 border-black bg-white">
            <button
              type="button"
              data-testid="history-tab-rides"
              onClick={() => setTab("rides")}
              className={
                "flex-1 py-1.5 rounded-xl border-2 border-black font-[Outfit] text-[12px] font-black uppercase tracking-wide transition " +
                (tab === "rides"
                  ? "bg-[#FF3B3B] text-white shadow-[0_3px_0_0_#000]"
                  : "bg-white text-black")
              }
            >
              Mes courses ({liveItems.length})
            </button>
            <button
              type="button"
              data-testid="history-tab-trips"
              onClick={() => setTab("trips")}
              className={
                "flex-1 py-1.5 rounded-xl border-2 border-black font-[Outfit] text-[12px] font-black uppercase tracking-wide transition " +
                (tab === "trips"
                  ? "bg-[#FF3B3B] text-white shadow-[0_3px_0_0_#000]"
                  : "bg-white text-black")
              }
            >
              Trajets publiés ({trips.length})
            </button>
          </div>
        )}

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3">
          {loading && (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-6 h-6 animate-spin text-[#FF3B3B]" />
            </div>
          )}

          {!loading && !isDriver && passengerItems.length === 0 && (
            <div className="text-center py-10 px-4">
              <Clock className="w-8 h-8 text-neutral-400 mx-auto mb-2" />
              <p className="font-[Outfit] text-[14px] text-neutral-600">
                Aucune demande pour le moment.
              </p>
            </div>
          )}

          {!loading && !isDriver && passengerItems.map((it, idx) => (
            <RideItem key={`${it.kind}-${it.id}-${idx}`} it={it} testid={`history-item-${idx}`} />
          ))}

          {!loading && isDriver && tab === "rides" && liveItems.length === 0 && (
            <div className="text-center py-10 px-4">
              <Clock className="w-8 h-8 text-neutral-400 mx-auto mb-2" />
              <p className="font-[Outfit] text-[14px] text-neutral-600">
                Aucune course pour le moment.
              </p>
            </div>
          )}
          {!loading && isDriver && tab === "rides" && liveItems.map((it, idx) => (
            <RideItem key={`live-${it.id}-${idx}`} it={it} testid={`history-live-${idx}`} />
          ))}

          {!loading && isDriver && tab === "trips" && trips.length === 0 && (
            <div className="text-center py-10 px-4">
              <Clock className="w-8 h-8 text-neutral-400 mx-auto mb-2" />
              <p className="font-[Outfit] text-[14px] text-neutral-600">
                Aucun trajet publié pour le moment.
              </p>
            </div>
          )}
          {!loading && isDriver && tab === "trips" && trips.map((t, idx) => (
            <PublishedTripItem key={`trip-${t.id}-${idx}`} t={t} testid={`history-trip-${idx}`} />
          ))}
        </div>
      </div>
    </div>
  );
}
